import { initAssetsDrmClient } from '@ghl-revex/assets-drm-client/setup'
import '@gohighlevel/ghl-ui/dist/style.css'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import './index.css'

import {
  ClientPortalApps,
  SwipeDirective,
  initializeApp,
} from '@ghl/clientportal-core/init'
import { DynamicScroller, RecycleScroller } from 'vue-virtual-scroller'
import Vue3TouchEvents, { Vue3TouchEventsOptions } from 'vue3-touch-events'

import { GesturePlugin } from '@vueuse/gesture'
import { createHead } from '@vueuse/head'
import DOMPurify from 'dompurify'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import VueGtag from 'vue-gtag'
import { createI18n } from 'vue-i18n'
import { createMetaManager } from 'vue-meta'
import App from './App.vue'
// import { SwipeDirective } from '@/directives/swipe.directive'
import da from '@/locales/da.json'
import de from '@/locales/de.json'
import en from '@/locales/en.json'
import es from '@/locales/es.json'
import fi from '@/locales/fi.json'
import frCa from '@/locales/fr_CA.json'
import frFr from '@/locales/fr_FR.json'
import it from '@/locales/it.json'
import nl from '@/locales/nl.json'
import no from '@/locales/no.json'
import ptBr from '@/locales/pt_BR.json'
import ptPt from '@/locales/pt_PT.json'
import sv from '@/locales/sv.json'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import mitt from 'mitt'
import { preventDefaultPlugin } from './plugins/preventDefaultPlugin'
import { router } from './router'
import { useGlobalStore } from './store/app'
import { requests } from './service/HTTPService'

export const emitter = mitt()

export const allowIframeAndAttributesConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: [
    'allow',
    'allowfullscreen',
    'frameborder',
    'scrolling',
    'src',
    'title',
    'width',
    'height',
    'referrerpolicy',
    'rel',
    'target',
  ],
}

export const i18n = createI18n({
  locale: 'en',
  globalInjection: true,
  legacy: false,
  runtimeOnly: true,
  messages: {
    en: en,
    de: de,
    es: es,
    da: da,
    fi: fi,
    'fr-CA': frCa,
    'fr-FR': frFr,
    it: it,
    nl: nl,
    no: no,
    'pt-BR': ptBr,
    'pt-PT': ptPt,
    sv: sv,
  },
})

// === Focus ring only for keyboard users (never for mouse users) ===
function handleFirstTab(e: KeyboardEvent) {
  if (e.key === 'Tab' || e.key?.startsWith('Arrow')) {
    document.body.classList.add('user-is-tabbing')
    window.removeEventListener('keydown', handleFirstTab)
    window.addEventListener('mousedown', handleMouseDownOnce)
  }
}
function handleMouseDownOnce() {
  document.body.classList.remove('user-is-tabbing')
  window.removeEventListener('mousedown', handleMouseDownOnce)
  window.addEventListener('keydown', handleFirstTab)
}
window.addEventListener('keydown', handleFirstTab)
;(async () => {
  const app = createApp(App)
  const sentryOptions = {
    release: __APP_BUILD_NUMBER__, // build number
    beforeSend: event => {
      if (event.tags?.customCode === 'true') {
        console.log('Ignoring error from customCode:', event.tags?.customCode)
        return null
      }
      return event
    },
  }

  await initializeApp(
    ClientPortalApps.COMMUNITIES,
    app as any,
    import.meta.env.VITE_JS_ENV,
    router as any,
    sentryOptions
  )
  defineCustomElements(window)
  app.provide('emitter', emitter)
  app.use(i18n)
  app.use(GesturePlugin)
  app.use<Vue3TouchEventsOptions>(Vue3TouchEvents, {
    disableClick: false,
  })
  app.use(createPinia())
  app.component('RecycleScroller', RecycleScroller)
  app.component('DynamicScroller', DynamicScroller)
  app.directive('swipe', SwipeDirective)
  app.directive('sanitized-html', {
    mounted(el, binding) {
      let html = binding.value
      let afterSanity
      if (typeof binding.value === 'object') {
        html = binding.value.html
        afterSanity = binding.value.afterSanity
      }
      const sanitizedHtml = DOMPurify.sanitize(html, {
        ...allowIframeAndAttributesConfig,
      })
      el.innerHTML = sanitizedHtml
      if (afterSanity) {
        afterSanity(el)
      }
    },
    updated(el, binding) {
      let html = binding.value
      let afterSanity
      if (typeof binding.value === 'object') {
        html = binding.value.html
        afterSanity = binding.value.afterSanity
      }
      const sanitizedHtml = DOMPurify.sanitize(html, {
        ...allowIframeAndAttributesConfig,
      })
      el.innerHTML = sanitizedHtml
      if (afterSanity) {
        afterSanity(el)
      }
    },
  })
  app.use(VueGtag, { bootstrap: false }, router)
  app.use(preventDefaultPlugin)
  const head = createHead()

  app.use(head)
  app.use(router as any)
  app.use(createMetaManager())
  app.mount('#app')
  const axiosInstance = requests()
  const globalStore = useGlobalStore()

  initAssetsDrmClient({
    env:
      import.meta.env.VITE_JS_ENV === 'production' ? 'production' : 'staging',
    source: 'communities',
    axiosInstance,
    accessMode: globalStore.isAuthenticated ? 'secure' : 'public',
  })

  // Note: setAccessMode has been removed from @ghl-revex/assets-drm-client
  // The access mode is set during initialization above
  // If reactive access mode updates are needed, the client needs to be re-initialized
})()
